import {
  ChipSize,
  ChipType,
  CovAutoComplete,
  CovChip,
  CovTextField,
} from "@cx-component-library/react-components";

/** One pickable option of a {@link MultiSelectChipField}. */
export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectChipFieldProps {
  label: string;
  placeholder?: string;
  /** Everything that can be picked — selected values included, so their chips keep a label. */
  options: MultiSelectOption[];
  /** The selected option values, in pick order. */
  value: string[];
  onChange: (value: string[]) => void;
  loading?: boolean;
  loadingText?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  /** Shown as the dropdown's only row when there is nothing left to pick. */
  noOptionsText?: string;
}

/**
 * Select-many dropdown whose picks show as removable chips inside the control
 * (ported from Customer Lounge). Built on `CovAutoComplete` with `multiple`, so
 * chips get backspace-to-remove and the chevron opens the list; the list stays
 * open while picking and hides what is already chosen.
 *
 * A value with no matching option still renders as a chip, labelled by the
 * value itself, so it can be removed.
 */
export const MultiSelectChipField = ({
  label,
  placeholder,
  options,
  value,
  onChange,
  loading = false,
  loadingText,
  disabled = false,
  error,
  helperText,
  noOptionsText,
}: MultiSelectChipFieldProps) => {
  const selected = value.map(
    (v) =>
      options.find((option) => option.value === v) ?? { value: v, label: v },
  );

  return (
    <CovAutoComplete
      multiple
      forcePopupIcon
      openOnFocus
      fullWidth
      disableCloseOnSelect
      disabled={disabled}
      loading={loading}
      loadingText={loadingText}
      noOptionsText={noOptionsText}
      options={options}
      value={selected}
      getOptionLabel={(option: MultiSelectOption) => option.label}
      isOptionEqualToValue={(a: MultiSelectOption, b: MultiSelectOption) =>
        a.value === b.value
      }
      filterSelectedOptions
      onChange={(_event, next: MultiSelectOption[]) =>
        onChange(next.map((option) => option.value))
      }
      renderTags={(tags: MultiSelectOption[], getTagProps) =>
        tags.map((tag, index) => {
          const { key, ...chipProps } = getTagProps({ index });
          return (
            <CovChip
              key={key}
              chipType={ChipType.GRAY}
              chipSize={ChipSize.MEDIUM}
              label={tag.label}
              {...chipProps}
            />
          );
        })
      }
      renderInput={(params) => (
        <CovTextField
          {...params}
          label={label}
          // Figma's 40px field (MUI "medium" would be 56px).
          size="small"
          placeholder={value.length === 0 ? placeholder : undefined}
          error={error}
          helperText={helperText}
        />
      )}
    />
  );
};
